import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit } from "@angular/core";
import { PoPageDynamicSearchFilters, PoPageDynamicSearchLiterals } from "@po-ui/ng-templates";
import { PoDisclaimer, PoMultiselectFilterMode, PoPageAction, PoTableColumn } from "@po-ui/ng-components";
import { Subject, Subscription, of } from "rxjs";
import { catchError, switchMap, takeUntil } from "rxjs/operators";

import { AccessAuditEntry, AccessAuditFilters, AccessAuditService } from "../services/access-audit.service";
import { Period, defaultPeriod } from "src/app/shared/period/period.util";
import { TableHeightFitter, auditActionOptions, auditEventLabel, auditActionTag, auditActorLabel, auditPeriodEnd, auditRoleTags, describeAudit, formatAuditWhen } from "../users-admin.utils";
import { RoleTag } from "src/app/shared/role-tags";
import { asList, disclaimerValue, disclaimerValues } from "../search-filters.util";

const PAGE_SIZE = 50;

interface AuditRow {
  createdAt: string;
  action: string;
  actionTag: RoleTag;
  roles: Array<RoleTag & { key: string }>;
  target: string;
  targetType: string;
  details: string;
  actor: string;
}

/** Trilha de alterações da gestão de usuários: quem mudou o quê e quando. */
@Component({
  selector: "app-access-audit",
  templateUrl: "./access-audit.component.html",
  // bloco: a altura da tabela é medida pelo fim do componente
  styles: [":host { display: block; }", ".audit-roles { display: flex; flex-wrap: wrap; gap: 4px; }"],
})
export class AccessAuditComponent implements OnInit, AfterViewInit, OnDestroy {
  // Filtros aplicados
  q = "";
  targetType = "";
  actions: Array<string> = [];
  actor = "";
  /** Recorte na tela principal (mesmo seletor do Smart Review); começa nos últimos 30 dias. */
  period: Period = defaultPeriod();

  items: Array<AuditRow> = [];
  total = 0;
  loading = false;
  loadingShowMore = false;

  /** Busca avançada nativa do PO: período, o que mudou, tipo e quem alterou. */
  readonly searchFilters: Array<PoPageDynamicSearchFilters> = [
    { property: "action", label: "O que mudou", optionsMulti: true, filterMode: PoMultiselectFilterMode.contains, gridColumns: 12, options: auditActionOptions() },
    {
      property: "targetType",
      label: "Tipo",
      gridColumns: 6,
      options: [
        { label: "Usuários", value: "user" },
        { label: "Áreas", value: "area" },
        { label: "Tribes", value: "tribe" },
      ],
    },
    { property: "actor", label: "Alterado por", gridColumns: 6 },
  ];

  readonly searchLiterals: PoPageDynamicSearchLiterals = {
    searchPlaceholder: "Buscar usuário, área ou tribe",
    filterTitle: "Busca avançada",
    filterConfirmLabel: "Aplicar",
    disclaimerGroupTitle: "Filtros ativos",
  };

  readonly pageActions: Array<PoPageAction> = [{ label: "Atualizar", icon: "po-icon-refresh", action: () => this.load() }];

  readonly columns: Array<PoTableColumn> = [
    { property: "createdAt", label: "Quando", width: "16%" },
    { property: "action", label: "O que mudou", width: "17%", type: "cellTemplate" },
    { property: "target", label: "Usuário, área ou tribe", width: "24%" },
    { property: "details", label: "Detalhes", width: "28%", type: "cellTemplate" },
    { property: "actor", label: "Alterado por", width: "15%" },
  ];

  /** A tabela ocupa o que sobra da tela: a página não rola, só as linhas. */
  readonly tableFit: TableHeightFitter;

  private readonly destroy$ = new Subject<void>();
  private readonly loadRequests = new Subject<void>();
  /** "Carregar mais" em andamento: cancelado quando a lista recarrega com outro filtro. */
  private showMoreSub?: Subscription;

  constructor(
    private auditService: AccessAuditService,
    elementRef: ElementRef<HTMLElement>,
    zone: NgZone,
  ) {
    this.tableFit = new TableHeightFitter(elementRef.nativeElement, zone);
    this.watchLoadRequests();
  }

  ngOnInit(): void {
    this.load();
  }

  ngAfterViewInit(): void {
    this.tableFit.start();
  }

  ngOnDestroy(): void {
    this.tableFit.stop();
    this.showMoreSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onQuickSearch(q: string): void {
    this.q = (q ?? "").trim();
    this.load();
  }

  onPeriodChange(period: Period): void {
    this.period = period;
    this.load();
  }

  onAdvancedSearch(filter: Record<string, any> | null): void {
    // A busca avançada do PO troca os disclaimers e limpa o campo de busca rápida
    this.q = "";
    this.actions = asList(filter?.["action"]);
    this.targetType = filter?.["targetType"] ?? "";
    this.actor = (filter?.["actor"] ?? "").trim();
    this.load();
  }

  onChangeDisclaimers(disclaimers: Array<PoDisclaimer>): void {
    this.q = disclaimerValue(disclaimers, "search") ?? "";
    this.actions = disclaimerValues(disclaimers, "action");
    this.targetType = disclaimerValue(disclaimers, "targetType") ?? "";
    this.actor = disclaimerValue(disclaimers, "actor") ?? "";
    this.load();
  }

  private get filters(): AccessAuditFilters {
    return {
      q: this.q || undefined,
      targetType: this.targetType || undefined,
      actor: this.actor || undefined,
      action: this.actions.length ? this.actions : undefined,
      from: this.period.dateFrom || undefined,
      to: auditPeriodEnd(this.period.dateTo),
    };
  }

  /** Só a última busca vale: uma resposta lenta de um filtro anterior não sobrescreve a atual. */
  private watchLoadRequests(): void {
    this.loadRequests
      .pipe(
        switchMap(() => this.auditService.list({ ...this.filters, offset: 0, limit: PAGE_SIZE }).pipe(catchError(() => of(null)))),
        takeUntil(this.destroy$),
      )
      .subscribe((page) => {
        this.loading = false;
        if (page) {
          this.items = page.items.map(toRow);
          this.total = page.total;
        }
      });
  }

  load(): void {
    this.showMoreSub?.unsubscribe();
    this.loadingShowMore = false;
    this.loading = true;
    this.loadRequests.next();
  }

  get allLoaded(): boolean {
    return this.items.length >= this.total;
  }

  onShowMore(): void {
    if (this.loadingShowMore || this.allLoaded) {
      return;
    }
    this.loadingShowMore = true;
    this.showMoreSub = this.auditService.list({ ...this.filters, offset: this.items.length, limit: PAGE_SIZE }).subscribe({
      next: (page) => {
        this.items = [...this.items, ...page.items.map(toRow)];
        this.total = page.total;
      },
      complete: () => (this.loadingShowMore = false),
      error: () => (this.loadingShowMore = false),
    });
  }
}

function toRow(entry: AccessAuditEntry): AuditRow {
  return {
    createdAt: formatAuditWhen(entry.createdAt),
    action: auditEventLabel(entry.action, entry.details),
    actionTag: auditActionTag(entry.action, entry.details),
    roles: auditRoleTags(entry),
    target: entry.target,
    targetType: entry.targetType,
    details: describeAudit(entry),
    actor: auditActorLabel(entry.actor),
  };
}
